import {
  createContext, ReactElement, ReactNode, useCallback, useContext, useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'contexts/newToastContext';
import { getOverviewThermalComfort } from '../helpers/overview';
import { FilterContext } from './FilterContext';
import { parseThermalComfortData, parseUnits } from '../helpers/thermalComfort';
import { ThermalComfortVariant } from '../ThermalComfort/components/UnitsInfo/types';

export interface ThermalComfortCountInfo {
  comfortable: number;
  belowLimit: number;
  aboveLimit: number;
  inoperative: number;
}

interface EnvironmentThermalCountInfo extends ThermalComfortCountInfo {
  outsideOperatingHours: number;
}

export type ThermalComfortUnitsInfo = {
  id: number;
  name: string;
  lat: number | null;
  lon: number | null;
  state: string;
  city: string;
  environments: EnvironmentThermalCountInfo;
  thermalComfort: ThermalComfortVariant;
}[]

export interface ThermalComfortDataContext {
  data: ThermalComfortCountInfo | undefined;
  units: ThermalComfortUnitsInfo;
  loading: boolean;
  refetch: (client: number, selectedOptions: FilterContext['selectedOptions']) => Promise<void>;
}

const ThermalComfortDataContext = createContext<ThermalComfortDataContext | undefined>(undefined);

export const ThermalComfortDataProvider = ({
  children,
}: Readonly<{ children: ReactNode }>): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ThermalComfortCountInfo | undefined>();
  const [units, setUnits] = useState<ThermalComfortUnitsInfo>([]);
  const { toast } = useToast();
  const { t } = useTranslation();

  const getThermalComfortInfo = useCallback(async (
    client: number,
    selectedOptions: FilterContext['selectedOptions'],
  ): Promise<void> => {
    setLoading(true);

    const data = await getOverviewThermalComfort(client, {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.group?.units,
      thermalComfortStatus: selectedOptions.status,
    });

    if (data) {
      setData(parseThermalComfortData(data));
      setUnits(parseUnits(data.UNITS));
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosConfortoTermico'), 'error');
    }

    setLoading(false);
  }, []);

  const value = useMemo((): ThermalComfortDataContext => ({
    data,
    loading,
    units,
    refetch: getThermalComfortInfo,
  }), [data, getThermalComfortInfo, loading, units]);

  return (
    <ThermalComfortDataContext.Provider value={value}>
      {children}
    </ThermalComfortDataContext.Provider>
  );
};

export const useThermalComfortDataContext = (): ThermalComfortDataContext => {
  const context = useContext(ThermalComfortDataContext);

  if (!context) {
    throw new Error('useThermalComfortDataContext must be used within a ThermalComfortDataProvider.');
  }

  return context;
};
